import React from "react";
import { forwardRef } from "react";
import MaterialTable from "material-table";
import AddBox from "@material-ui/icons/AddBox";
import ArrowUpward from "@material-ui/icons/ArrowUpward";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import { FormattedMessage } from "react-intl";
import messages_en from "../../translations/en.json";
import messages_br from "../../translations/br.json";
import messages_es from "../../translations/es.json";
import { connect } from "react-redux";
import Number from "../../custom/Number";
import { getLCTCodeByTaxResidence } from "../../actions";
import DateFormatter from "./DateFormatter";
import { getNestedValue } from "../../util";
import moment from "moment";

const UNIT_FONT_SIZE = "60%";
const DARK_FONT_COLOR = "#999";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const messages = {
  en: messages_en,
  br: messages_br,
  es: messages_es,
};

const titleIsLocalizable = (title, user) => user && title && title.startsWith && title.startsWith("app.") && messages[user.locale];

const localizeObjectProps = (obj, user) => {
  if (obj && user) {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value == "object") {
        localizeObjectProps(value, user);
      } else if (titleIsLocalizable(value, user)) {
        obj[key] = messages[user.locale][value];
      }
    }
  }
  return obj;
};

const AbtcMaterialTable = (props) => {
  const newProps = {
    ...props,
    icons: tableIcons,
    title: props.preserveTitle ? props.title : props.title && <FormattedMessage id={props.title} />,
    columns: props.columns.map((c) => ({
      ...c,
      title: titleIsLocalizable(c.title, props.user) ? messages[props.user.locale][c.title] : c.title,
    })),
    localization: localizeObjectProps(props.localization, props.user),
    components: {
      ...(props.components || {}),
      Container: props?.components?.Container || ((props) => <div>{props.children}</div>),
    },
  };
  return <MaterialTable {...newProps} />;
};

export default AbtcMaterialTable;

export const TableList = AbtcMaterialTable;

export const newColumn = (title, field, type, align, render, options = {}) => ({
  title,
  field,
  type,
  cellStyle: {
    textAlign: align,
    fontSize: 12,
  },
  headerStyle: {
    textAlignLast: align,
    fontSize: 12,
  },
  render,
  ...options,
});

export const newColumnConfig = ({ title, field, type = "string", align = "left", render, options = {} }) => newColumn(title, field, type, align, render, options);

export const newColumnText = (title, field, render, align = "left") => newColumn(title, field, "string", align, render);

export const newColumnDate = (title, field, options) => newColumn(title, field, "date", "center", "", options);

export const newColumnDateByMoment = (title, field, options = {}) =>
  newColumn(title, field, "string", "center",
    (rowData) => {
      const date = rowData[field];

      if (!date) return "";

      return moment(date).format("DD/MM/YYYY");
    },
    options
  );

export const newColumnDateByLocale = (title, field, options) => {
  return newColumn(title, field, "date", "center", (rowData) => <DateFormatter date={rowData[field]} />, options);
};

export const newColumnNumeric = (title, field) => newColumn(title, field, "numeric", "right");

export const newColumnStatus = (title, field, names) => newColumn(title, field, "string", "center", (rowData) => names[rowData[field]]);

export const newColumnByNestedProps = (title, field, type = "string", align = "left", render, options = {}) => {
  // Função para pegar valor via path
  // exemplo: buscando o id em uma prop aninhada "contract.id"
  const getValueByPath = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);

  return newColumn(
    title,
    field,
    type,
    align,
    render || ((rowData) => getValueByPath(rowData, field)),
    options
  );
};

/**
 * 
 * @param {string} title Título da coluna exibido na tabela
 * @param {string} field nome do campo que possui o valor
 * @param {boolean} calculate indica se deve ser calculado a porcentagem ou não 
 * @returns {React.ReactNode} JSX contendo o valor formatado
 */
export const newColumnPercentage = (title, field, calculate = false) => {
  const getValue = (value) => (calculate ? value / 100 : value);

  return newColumn(title, field, "numeric", "rigth", (rowData) => {
    const value = rowData[field];

    if (value === null || value === undefined) return null;

    return (
      <Number
        value={getValue(value)}
        display="percentage"
        canCopySufix={false}
      />
    );
  });
};

export const newColumnUSDt = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(2)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>USDT</span>
    </>
  ));

export const newColumnCOPD = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(2)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>COPD</span>
    </>
  ));

export const newColumnCLPD = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(2)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>CLPD</span>
    </>
  ));

export const newColumnBRZ = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(2)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>BRZ</span>
    </>
  ));

export const newColumnBRL = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>R$</span> {rowData[field] && rowData[field].toFixed(2)}
    </>
  ));

export const newColumnBTC = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(8)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>BTC</span>
    </>
  ));

export const newColumnETH = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(8)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>ETH</span>
    </>
  ));

export const newColumnGGP = (title, field) =>
  newColumn(title, field, "numeric", "right", (rowData) => (
    <>
      {rowData[field] && rowData[field].toFixed(2)} <span style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>COPD</span>
    </>
  ));

/**
 *
 * @param {string} title Título da coluna exibido na tabela
 * @param {string} field nome do campo que possui o valor
 * @param {string} taxResidence código do pais para determinar a moeda baseado no componente de countrys de `./components/Terms`
 * @param {boolean} [hiddenValues=false] - Se valor for `true`, o valor apresentará os bullets ao inves do valor de fato
 * @param {boolean} [showNotAvailableForZero=false] - Se for `true`, valores iguais a 0 serão exibidos como `N/D`.
 * @param {string} [customTaxResidenceRowDataField="taxResidence"] - Campo do rowData onde o componente irá buscar a taxResidence caso o parâmetro taxResidence não seja fornecido
 * @returns {React.ReactNode} JSX contendo o valor formatado
 */
export const newColumnValueWithNumberComponent = (
  title,
  field,
  taxResidence = "",
  hiddenValues = false,
  showNotAvailableForZero = false,
  customTaxResidenceRowDataField = "taxResidence",
  receiverField,
) =>
  newColumn(title, field, "numeric", "rigth", (rowData) => {
    const value = rowData[field];

    if (value === null || value === undefined) {
      return null;
    }
    if (showNotAvailableForZero && value === 0) {
      return <FormattedMessage id="app.notAvailable" />;
    }

    const finalTaxResidence = taxResidence || (rowData?.[customTaxResidenceRowDataField] ?? "LCT");

    const currencyCode = getLCTCodeByTaxResidence(finalTaxResidence);
    const finalCurrency = currencyCode && currencyCode !== "*" ? currencyCode : "LCT";

    const receiver = receiverField ? getNestedValue(rowData, receiverField) : null;
    return (
      <>
        <Number
          canCopySufix={false}
          currency={finalCurrency}
          hideValue={hiddenValues}
          value={value}
        />
        {receiver && <span style={{ color: DARK_FONT_COLOR }}>{`(${receiver})`}</span>}
      </>
    );
  });

export const newColumnValue = (title, field) => newColumn(title, field, "numeric", "right", (rowData) => <>{rowData[field] && rowData[field].toFixed(2)}</>);

export const newColumnMoney = (title, field, filterTaxResidence) => {
  if (filterTaxResidence == "Brasil") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Colômbia") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Chile") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Argentina") {
    return newColumnValue(title, field);
  } else if (filterTaxResidence == "Austrália") {
    return newColumnValue(title, field);
  } else if (filterTaxResidence == "Uruguai") {
    return newColumnValue(title, field);
  } else if (filterTaxResidence == "Venezuela") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "México") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Equador") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Índia") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Nova Zelândia") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Panamá") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "EUA") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Costa Rica") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Guernsey") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Espanha") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "França") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Reino Unido") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Irlanda") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Suiça") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Itália") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Alemanha") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Grécia") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Países Baixos") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Bélgica") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Hungria") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Chipre") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Luxemburgo") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Suécia") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Emirados Árabes") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Peru") {
    return newColumnUSDt(title, field);
  } else if (filterTaxResidence == "Paraguai") {
    return newColumnUSDt(title, field);
  } else {
    return newColumn(title, field, "numeric", "right", (rowData) => (
      <>
        {rowData[field] && rowData[field].toFixed(2)} <small style={{ fontSize: UNIT_FONT_SIZE, color: DARK_FONT_COLOR }}>{rowData?.account?.user?.taxCurrency ?? rowData?.user?.taxCurrency}</small>
      </>
    ));
  }
};
